/**
 * Cause Report Utilities
 */

import type { FilterConfig } from '../../../records/filter';
import { VALID_DATE_RANGE_PRESETS } from '../../../records/filter/constants/filter.constants';
import type { ConductDimensionId, HighRiskRecord } from '../types';

/** Valid conduct dimension IDs */
const VALID_DIMENSIONS = new Set<string>(['foreseeable_harm', 'customer_strain', 'dissatisfaction']);

/**
 * Validate and filter dimension strings to known ConductDimensionId values.
 * Prevents runtime crashes from unexpected API data.
 */
export function validateDimensions(dims: string[]): ConductDimensionId[] {
  return dims.filter((d): d is ConductDimensionId => VALID_DIMENSIONS.has(d));
}

/** API record shape (snake_case) shared across hooks */
export interface ApiRecord {
  id: string;
  conduct_score: number;
  summary: string;
  customer_name: string;
  date: string;
  journey_stage?: string;
  channel?: string;
  product?: string;
  root_cause_labels: Array<{ id: string; name: string }>;
  dimensions: string[];
}

/**
 * Transform a snake_case API record to the camelCase HighRiskRecord type.
 * Validates dimensions to prevent crashes from unexpected values.
 */
export function transformRecord(rec: ApiRecord): HighRiskRecord {
  return {
    id: rec.id,
    conductScore: rec.conduct_score,
    summary: rec.summary,
    customerName: rec.customer_name,
    date: rec.date,
    journeyStage: rec.journey_stage,
    channel: rec.channel,
    product: rec.product,
    rootCauseLabels: rec.root_cause_labels,
    dimensions: validateDimensions(rec.dimensions),
  };
}

/**
 * Map FilterConfig to API query parameters (snake_case).
 * Converts frontend filter state to the format expected by the API.
 */
export function mapFiltersToApiParams(
  filters: FilterConfig
): Record<string, string> {
  const params: Record<string, string> = {};

  // Date range. Only forward a KNOWN preset — an unknown/legacy value (e.g. a
  // stale `all_time` from an old deep link) means "all time / no filter", so we
  // omit the param and let the backend apply no date bound, rather than have it
  // 400 on a preset its enum doesn't recognise.
  if (
    filters.dateRange?.preset &&
    (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(filters.dateRange.preset)
  ) {
    params.date_range_preset = filters.dateRange.preset;
  }
  if (filters.dateRange?.from) {
    params.custom_date_from = filters.dateRange.from;
  }
  if (filters.dateRange?.to) {
    params.custom_date_to = filters.dateRange.to;
  }

  // Dimensions
  if (filters.dimensions?.values?.length) {
    params.dimensions = filters.dimensions.values.join(',');
    if (filters.dimensions.logic) {
      params.dimensions_logic = filters.dimensions.logic;
    }
  }

  // Search
  if (filters.search) {
    params.search = filters.search;
  }

  // Conduct score range
  if (filters.conductScoreMin !== undefined) {
    params.conduct_score_min = String(filters.conductScoreMin);
  }
  if (filters.conductScoreMax !== undefined) {
    params.conduct_score_max = String(filters.conductScoreMax);
  }

  return params;
}
